/**
 * Displays items the current user is currently borrowing.
 *
 * Shows active loans, proposals awaiting acceptance, and returns awaiting
 * confirmation. Each card includes action buttons relevant to the loan status.
 * Renders nothing when the user has no active borrows.
 */
import { useState } from 'react';
import { useMyBorrowedItems, BorrowedItem } from '@/hooks/useMyBorrowedItems';
import {
  useCancelReturn,
  useAcceptLoanProposal,
  useCancelLoanProposal,
} from '@/hooks/useLoans';
import { toast } from 'sonner';
import { Check, X, RotateCcw, FileText } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { T, RADIUS, SHADOW, CATEGORY_EMOJI, CAT_FALLBACK } from '@/lib/tokens';
import { useAuth } from '@/contexts/AuthContext';
import { DueDateBadge, getDueStatus } from '@/components/design/DueDateBadge';
import { ConfirmReturnDialog } from '@/components/ConfirmReturnDialog';

/**
 *
 * @param root0
 * @param root0.status
 */
function StatusPill({ status }: { status: string }) {
  const config = (() => {
    if (status === 'PENDING_ACCEPTANCE')
      return {
        bg: T.accentSoft,
        text: T.accent,
        border: T.accentBorder,
        label: 'Awaiting your acceptance',
      };
    if (status === 'RETURN_PENDING')
      return {
        bg: T.warningSoft,
        text: T.warning,
        border: T.warningBorder,
        label: 'Return pending',
      };
    // Overdue visual treatment is handled by DueDateBadge
    return { bg: T.successSoft, text: T.success, border: T.successBorder, label: 'Active' };
  })();

  return (
    <span
      style={{
        flexShrink: 0,
        padding: '3px 8px',
        borderRadius: RADIUS.xl,
        fontSize: 11,
        fontWeight: 600,
        background: config.bg,
        color: config.text,
        border: `1px solid ${config.border}`,
      }}
    >
      {config.label}
    </span>
  );
}

/**
 *
 * @param root0
 * @param root0.item
 * @param root0.currentUserId
 */
function BorrowedItemCard({
  item,
  currentUserId,
  onOpenReturnDialog,
}: {
  item: BorrowedItem;
  currentUserId: string;
  onOpenReturnDialog: (loanId: string, itemName: string, mode: 'initiate' | 'confirm') => void;
}) {
  const cancelReturn = useCancelReturn();
  const acceptProposal = useAcceptLoanProposal();
  const cancelProposal = useCancelLoanProposal();

  const emoji = item.category ? (CATEGORY_EMOJI[item.category] ?? '📦') : '📦';
  const catColours = item.category && T.cat[item.category] ? T.cat[item.category] : CAT_FALLBACK;
  const isPending =
    cancelReturn.isPending ||
    acceptProposal.isPending ||
    cancelProposal.isPending;

  const isReturnPending = item.status === 'RETURN_PENDING';
  const isPendingAcceptance = item.status === 'PENDING_ACCEPTANCE';
  const returnInitiatedByMe = item.return_initiated_by === currentUserId;

  const handleCancelReturn = async () => {
    try {
      await cancelReturn.mutateAsync({ loanId: item.loan_id });
      toast.success('Return cancelled');
    } catch {
      /* handled by hook */
    }
  };

  const handleAccept = async () => {
    try {
      await acceptProposal.mutateAsync({ loanId: item.loan_id });
      toast.success('Loan accepted');
    } catch {
      /* handled by hook */
    }
  };

  const handleDecline = async () => {
    try {
      await cancelProposal.mutateAsync({ loanId: item.loan_id });
      toast.success('Loan declined');
    } catch {
      /* handled by hook */
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.sm,
      }}
    >
      {/* Item thumbnail */}
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: RADIUS.sm,
          background: catColours.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: 20,
        }}
      >
        {emoji}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: T.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.item_name}
          </span>
          <StatusPill status={item.status} />
          {item.status === 'ACTIVE' && <DueDateBadge dueAt={item.due_at} />}
          {item.notes && <FileText size={14} color={T.textMuted} style={{ flexShrink: 0 }} />}
        </div>
        <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
          From {item.owner_name ?? 'Unknown'}
          {item.due_at && (
            <>
              {' '}
              &middot; Due{' '}
              {new Date(item.due_at).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
              })}
            </>
          )}
          {!item.due_at && item.status === 'ACTIVE' && (
            <>
              {' '}
              &middot; Borrowed{' '}
              {formatDistanceToNow(new Date(item.borrowed_at), { addSuffix: true })}
            </>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {isPendingAcceptance && (
          <>
            <button
              onClick={handleDecline}
              disabled={isPending}
              aria-label="Decline"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: RADIUS.sm,
                border: `1px solid ${T.border}`,
                background: T.surface,
                cursor: isPending ? 'default' : 'pointer',
                color: T.textSec,
                opacity: isPending ? 0.6 : 1,
              }}
            >
              <X size={14} />
            </button>
            <button
              onClick={handleAccept}
              disabled={isPending}
              aria-label="Accept"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: 'none',
                background: T.accent,
                boxShadow: SHADOW.btn.accent,
                cursor: isPending ? 'default' : 'pointer',
                color: T.white,
                opacity: isPending ? 0.6 : 1,
              }}
            >
              <Check size={14} />
            </button>
          </>
        )}

        {item.status === 'ACTIVE' && (
          <button
            onClick={() => onOpenReturnDialog(item.loan_id, item.item_name, 'initiate')}
            disabled={isPending}
            aria-label="Initiate return"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: RADIUS.sm,
              border: `1px solid ${T.border}`,
              background: T.surface,
              cursor: isPending ? 'default' : 'pointer',
              color: T.textSec,
              opacity: isPending ? 0.6 : 1,
            }}
          >
            <RotateCcw size={14} />
          </button>
        )}

        {isReturnPending && !returnInitiatedByMe && (
          <button
            onClick={() => onOpenReturnDialog(item.loan_id, item.item_name, 'confirm')}
            disabled={isPending}
            aria-label="Confirm return"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: 'none',
              background: T.accent,
              boxShadow: SHADOW.btn.accent,
              cursor: isPending ? 'default' : 'pointer',
              color: T.white,
              opacity: isPending ? 0.6 : 1,
            }}
          >
            <Check size={14} />
          </button>
        )}

        {isReturnPending && (
          <button
            onClick={handleCancelReturn}
            disabled={isPending}
            aria-label="Cancel return"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: RADIUS.sm,
              border: `1px solid ${T.border}`,
              background: T.surface,
              cursor: isPending ? 'default' : 'pointer',
              color: T.textSec,
              opacity: isPending ? 0.6 : 1,
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 *
 */
export function BorrowedItemsList() {
  const { user } = useAuth();
  const { data: borrowedItems, isLoading, isError } = useMyBorrowedItems();

  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnDialogLoanId, setReturnDialogLoanId] = useState<string | null>(null);
  const [returnDialogItemName, setReturnDialogItemName] = useState('');
  const [returnDialogMode, setReturnDialogMode] = useState<'initiate' | 'confirm'>('initiate');

  const openReturnDialog = (loanId: string, itemName: string, mode: 'initiate' | 'confirm') => {
    setReturnDialogLoanId(loanId);
    setReturnDialogItemName(itemName);
    setReturnDialogMode(mode);
    setReturnDialogOpen(true);
  };

  if (isLoading || isError || !borrowedItems || borrowedItems.length === 0) return null;

  const sorted = [...borrowedItems].sort((a, b) => {
    const order: Record<string, number> = { PENDING_ACCEPTANCE: 0, RETURN_PENDING: 1, ACTIVE: 2 };
    const statusDiff = (order[a.status] ?? 3) - (order[b.status] ?? 3);
    if (statusDiff !== 0) return statusDiff;

    // Within ACTIVE loans, sort overdue/due-today to top
    if (a.status === 'ACTIVE' && b.status === 'ACTIVE') {
      const aDue = getDueStatus(a.due_at);
      const bDue = getDueStatus(b.due_at);
      const priority = (s: string | null) => {
        if (s === 'overdue') return 0;
        if (s === 'due-today') return 1;
        return 2;
      };
      const dueDiff = priority(aDue) - priority(bDue);
      if (dueDiff !== 0) return dueDiff;
      // Both overdue — most overdue first
      if (aDue === 'overdue' && bDue === 'overdue' && a.due_at && b.due_at) {
        return new Date(a.due_at).getTime() - new Date(b.due_at).getTime();
      }
    }

    return 0;
  });

  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.lg,
        boxShadow: SHADOW.md,
        marginBottom: 16,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '14px 16px 12px',
          borderBottom: `1px solid ${T.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600, color: T.text }}>Items You're Borrowing</div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: RADIUS.dot,
            background: T.accent,
            color: T.white,
            letterSpacing: '0.03em',
          }}
        >
          {borrowedItems.length}
        </span>
      </div>

      {/* Borrowed items list */}
      <div style={{ padding: '12px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sorted.map((item) => (
          <BorrowedItemCard
            key={item.loan_id}
            item={item}
            currentUserId={user!.id}
            onOpenReturnDialog={openReturnDialog}
          />
        ))}
      </div>

      <ConfirmReturnDialog
        loanId={returnDialogLoanId}
        itemName={returnDialogItemName}
        mode={returnDialogMode}
        open={returnDialogOpen}
        onOpenChange={setReturnDialogOpen}
      />
    </div>
  );
}
